<template>
  <div class="media-library-panel">
    <div class="panel-header">
      <h3 class="panel-title">Media Library</h3>
      <div v-if="replaceMode" class="replace-mode-banner">
        <b-icon icon="swap-horizontal" size="is-small"></b-icon>
        <span>Click an asset to use as replacement</span>
      </div>
      <div class="type-tabs">
        <button
          v-for="tab in mediaTabs"
          :key="tab.value"
          class="type-tab"
          :class="{ 'is-active': selectedType === tab.value }"
          @click="selectType(tab.value)"
        >
          <b-icon :icon="tab.icon" size="is-small"></b-icon>
          <span>{{ tab.label }}</span>
          <span v-if="getTypeCount(tab.value) > 0" class="tab-count">{{ getTypeCount(tab.value) }}</span>
        </button>
      </div>
      <b-field>
        <b-input
          v-model="searchQuery"
          placeholder="Search..."
          size="is-small"
          icon="magnify"
        ></b-input>
      </b-field>
    </div>

    <div class="panel-body">
      <div v-if="loading" class="has-text-centered" style="padding: 20px;">
        <b-loading :active="true" :is-full-page="false"></b-loading>
      </div>

      <div v-else-if="filteredMedia.length === 0" class="empty-state">
        <p>No {{ selectedType.toUpperCase() }} media found</p>
      </div>

      <div v-else class="media-list">
        <div
          v-for="media in filteredMedia"
          :key="media.id"
          class="media-item"
          :class="{ 'replace-mode-item': replaceMode }"
          :draggable="!replaceMode"
          @dragstart="onDragStart($event, media)"
          @dragend="onDragEnd"
          @click="onItemClick(media)"
        >
          <div class="media-item-content">
            <div class="media-icon">
              <b-icon :icon="getMediaIcon(media.media_type)" size="is-small"></b-icon>
            </div>
            <div class="media-info">
              <div class="media-title">
                <span
                  class="tx-status-dot-inline"
                  :class="isMediaTxReady(media) ? 'tx-ready' : 'tx-missing'"
                  :title="isMediaTxReady(media) ? 'TX file present — ready to drop' : 'No TX file linked yet'"
                ></span>
                {{ media.title }}
              </div>
              <div class="media-duration">{{ formatDuration(media.duration) }}</div>
            </div>
          </div>
          <div class="drag-handle">
            <b-icon icon="drag-vertical" size="is-small"></b-icon>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MediaApi from '@/api/media'
import { isMediaTxReady } from '@/utils/mediaStatus'

export default {
  name: 'MediaLibraryPanel',
  props: {
    channelId: {
      type: Number,
      required: true
    },
    channelName: {
      type: String,
      default: ''
    },
    replaceMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedType: 'tvc',
      searchQuery: '',
      media: [],
      allMedia: [], // Store all shortform media for counts
      loading: false,
      mediaTabs: [
        { value: 'tvc', label: 'TVC', icon: 'television-classic' },
        { value: 'promo', label: 'Promo', icon: 'bullhorn' },
        { value: 'filler', label: 'Filler', icon: 'image-multiple' },
        { value: 'gfx', label: 'GFX', icon: 'watermark' }
      ]
    }
  },
  computed: {
    filteredMedia() {
      if (!this.searchQuery) return this.media

      const query = this.searchQuery.toLowerCase()
      return this.media.filter(m =>
        m.title.toLowerCase().includes(query)
      )
    }
  },
  async mounted() {
    await this.fetchAllMedia()
  },
  methods: {
    isMediaTxReady,
    async fetchAllMedia() {
      this.loading = true
      try {
        // Fetch all media once and filter locally
        const allMedia = await MediaApi.getAll()

        // Store shortform media filtered by channel assignment
        // Media must have this channel in metadata.channels or metadata.suppliers
        const chName = (this.channelName || '').toLowerCase()
        this.allMedia = allMedia.filter(m => {
          if (!m.media_type || !['tvc', 'promo', 'filler', 'gfx'].includes(m.media_type.toLowerCase())) return false
          // If no channel name to filter by, show all
          if (!chName) return true
          // Parse metadata if string
          const meta = typeof m.metadata === 'string' ? JSON.parse(m.metadata || '{}') : (m.metadata || {})
          const channels = (meta.channels || []).map(c => (typeof c === 'string' ? c : c.name || '').toLowerCase())
          const suppliers = (meta.suppliers || []).map(s => s.toLowerCase())
          return channels.includes(chName) || suppliers.includes(chName)
        })

        // Filter by current selected type
        this.filterMediaByType()
      } catch (error) {
        console.error('Failed to fetch media:', error)
        this.$buefy.toast.open({
          message: 'Failed to load media library',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },

    filterMediaByType() {
      this.media = this.allMedia.filter(m =>
        m.media_type && m.media_type.toLowerCase() === this.selectedType.toLowerCase()
      )
    },

    selectType(type) {
      this.selectedType = type
      this.filterMediaByType()
    },

    getTypeCount(type) {
      return this.allMedia.filter(m =>
        m.media_type && m.media_type.toLowerCase() === type.toLowerCase()
      ).length
    },

    onItemClick(media) {
      if (this.replaceMode) {
        // In replace mode, emit the selected replacement media
        this.$emit('replacement-selected', {
          media_id: media.id,
          title: media.title,
          duration: media.duration,
          media_type: media.media_type
        })
      }
    },

    onDragStart(event, media) {
      // Store media data in drag event
      event.dataTransfer.effectAllowed = 'copy'
      // duration_seconds is the canonical field — backend getAll computes it
      // from duration_ms (probed) preferred, falling back to mediaLength with
      // media_type-aware unit conversion. Frontend should NOT recompute units.
      event.dataTransfer.setData('application/json', JSON.stringify({
        media_id: media.id,
        title: media.title,
        duration: media.duration, // legacy raw mediaLength — kept for back-compat
        duration_seconds: media.duration_seconds, // canonical, always in seconds
        media_type: media.media_type
      }))

      // Visual feedback
      event.target.classList.add('dragging')

      // Emit whole media object — parent reads duration_seconds for preview.
      this.$emit('drag-start', media)
    },

    onDragEnd(event) {
      event.target.classList.remove('dragging')

      // Emit to parent
      this.$emit('drag-end')
    },

    getMediaIcon(type) {
      const icons = {
        tvc: 'television-classic',
        promo: 'bullhorn',
        filler: 'image-multiple',
        gfx: 'watermark'
      }
      return icons[type] || 'file-video'
    },

    formatDuration(seconds) {
      if (!seconds) return 'N/A'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${String(secs).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.media-library-panel {
  background: white;
  border-right: 1px solid #dbdbdb;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 15px;
  border-bottom: 1px solid #dbdbdb;
  background: #f5f5f5;
  z-index: 20;
  position: relative;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #363636;
}

.type-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.type-tab {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #4a4a4a;
  transition: all 0.2s;
}

.type-tab:hover {
  border-color: #3273dc;
  color: #3273dc;
}

.type-tab.is-active {
  background: #3273dc;
  border-color: #3273dc;
  color: white;
}

.type-tab.is-active .tab-count {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.tab-count {
  font-size: 10px;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 10px;
  color: #7a7a7a;
}

.replace-mode-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #3273dc;
  color: white;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 12px;
  font-weight: 500;
}

.media-item.replace-mode-item {
  border-color: #3273dc;
  cursor: pointer;
}

.media-item.replace-mode-item:hover {
  background: #e8f0fe;
  border-color: #2c5fc7;
  box-shadow: 0 2px 8px rgba(50, 115, 220, 0.3);
}

.panel-body {
  flex: 1;
  min-height: 0; /* Allow shrinking below content height so overflow-y scroll engages */
  overflow-y: auto;
  padding: 10px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #7a7a7a;
}

.media-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.media-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  cursor: grab;
  transition: all 0.2s;
}

.media-item:hover {
  border-color: #3273dc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.media-item.dragging {
  opacity: 0.5;
  cursor: grabbing;
}

.media-item-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.media-icon {
  color: #3273dc;
}

.media-info {
  flex: 1;
  min-width: 0;
}

.media-title {
  font-size: 13px;
  font-weight: 500;
  color: #363636;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.media-duration {
  font-size: 11px;
  color: #7a7a7a;
  margin-top: 2px;
}

/* TX-readiness dot prefixed to the media title in the library panel.
   Same colors as the week-view episode dot and the day-view timeline dot.
   Tells the operator at-a-glance which assets have a usable file before
   they drag onto the timeline. */
.tx-status-dot-inline {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.3);
  margin-right: 5px;
  vertical-align: middle;
  flex-shrink: 0;
}
.tx-status-dot-inline.tx-ready {
  background-color: #22c55e;
}
.tx-status-dot-inline.tx-missing {
  background-color: #ef4444;
}

.drag-handle {
  color: #b5b5b5;
  cursor: grab;
}

.media-item:active .drag-handle {
  cursor: grabbing;
}
</style>
